import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { OversiktRapporterQuery_node_TEDSYSReportNew__Associations_Params } from '../../Features/Delsystem/SystemAdministration/ObjektVyer/OversiktRapporter/Queries/_QueryTypes/OversiktRapporterQuery';
import { AvenyCollectionMap } from '../../Model/ModelContext/ModelContext';
import {
  getCollectionInfo,
  getAttributes,
  getAssociations,
} from '../../Model/avenyModelUtils';

// Define the structure of a model tree node
export interface IModelTreeNode {
  name: string;
  path: string;
  displayName: string;
  exportable: boolean;
  isExtendedAssociation?: boolean;
  children?: IModelTreeNode[];
}

// Define the context value interface
export type IModelTreeContextValue = IModelTreeNode[] | null;

// Create the context with default values
export const ModelTreeContext = createContext<IModelTreeContextValue>(null);

// Provider props interface
export interface IModelTreeProviderProps {
  children: ReactNode;
  ShortCollectionName: string;
  infoFields: IModelTreeNode[];
  Params: OversiktRapporterQuery_node_TEDSYSReportNew__Associations_Params[];
  avenyCollectionMap: AvenyCollectionMap;
}

// Create the provider component
export const ModelTreeProvider: React.FC<IModelTreeProviderProps> = ({
  children,
  ShortCollectionName,
  infoFields,
  Params,
  avenyCollectionMap,
}) => {
  const modelTree: IModelTreeNode[] = useMemo(
    () => [
      {
        name: 'info',
        path: '@@info',
        displayName: 'Information',
        exportable: true,
        children: infoFields,
      },
      ...(Params?.length
        ? [
            {
              name: 'params',
              path: '@@params',
              displayName: 'Parametrar',
              exportable: true,
              children: Params?.map(param => ({
                name: param.NAMN ?? '',
                path: param.NAMN && param.NAMN !== '' ? ':' + param.NAMN : '',
                displayName: param.NAMN ?? '',
                exportable: true,
              })),
            },
          ]
        : []),
      ...createModelTree(ShortCollectionName, avenyCollectionMap),
    ],
    [ShortCollectionName, Params, infoFields, avenyCollectionMap]
  );
  return (
    <ModelTreeContext.Provider value={modelTree}>
      {children}
    </ModelTreeContext.Provider>
  );
};

// Custom hook to use the model tree context
export const useModelTree = (): IModelTreeNode[] => {
  const context = useContext(ModelTreeContext);
  if (context === null) {
    throw new Error('useModelTree must be used within a ModelTreeProvider');
  }
  return context;
};

// Model tree creation function

const getCollectionAttributeNodes = (
  collectionName: string,
  model: AvenyCollectionMap,
  depth: string[],
  path: string
): IModelTreeNode[] => {
  if (collectionName && !depth.includes(collectionName) && depth.length < 5) {
    const collection = getCollectionInfo(collectionName, '', model)?.collection;
    return [
      ...(getAttributes(collection) ?? [])
        .filter(attr => attr.Attribute !== 'None')
        .map(
          attr =>
            ({
              name: attr.AttributeName,
              path: path + attr.AttributeName,
              displayName: attr.DisplayLabel,
              exportable: attr.Attribute === 'Normal',
            } as IModelTreeNode)
        ),
      ...(getAssociations(collection) ?? [])
        .filter(ass => ass.Attribute !== 'None')
        .map(
          ass =>
            ({
              name: ass.AssociationName,
              path: path + ass.AssociationName,
              displayName: ass.DisplayLabel,
              exportable: ass.Attribute === 'Normal',
              isExtendedAssociation: !ass.JoinAssociation,
              children: getCollectionAttributeNodes(
                ass.AssociatedCollectionName,
                model,
                [...depth, collectionName],
                path + ass.AssociationName + '.'
              ),
            } as IModelTreeNode)
        ),
    ];
  }
  return [];
};

export const createModelTree = (
  collectionName: string,
  model: AvenyCollectionMap
): IModelTreeNode[] =>
  getCollectionAttributeNodes(collectionName, model, [], '');
